/**
 * Funnel Visualization - MA-ARCH-164 Refactored
 * 
 * Premium decision tree view of funnel structure.
 * 
 * V4.0 (MA-ARCH-164): Major refactor
 *   - Extracted components: VisualizationNode, ConnectorLine, BranchLabel, VisualizationLegend
 *   - Extracted hooks: useFunnelViewport, useFunnelInteractions
 *   - Extracted utils: graphMapping, layoutConstants, nodeConfig
 *   - Main component is now a lean orchestrator
 * 
 * Previous versions:
 * V3.6 (MA-UI-133): Synchronized hover-exit geometry preservation (STABLE)
 * V3.5 (MA-UI-131): Linear funnel rendering fix
 * V3.4 (MA-UI-130): Connector geometry correction
 * V3.3 (MA-UI-129): TRUE DOM-based clearance using actual rendered measurements
 * V3.2 (MA-UI-128): Dynamic geometry-based clearance calculation
 * V3.1 (MA-UI-127): Hover-aware clearance behavior
 * V3.0 (MA-UI-120): Final polish pass - production quality
 * 
 * Design principles:
 * - Funnel-first: Show user journey, not raw graph
 * - Compact default state, rich hover preview
 * - Natural downward navigation
 * - System consistency (matches Steps panel)
 * - Premium feel
 */

import React, { useMemo, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { GitBranch } from 'lucide-react';

// Extracted components
import { VisualizationNode, ConnectorLine, BranchLabel, VisualizationLegend } from './components';

// Extracted hooks
import { useFunnelViewport, useFunnelInteractions } from './hooks';

// Extracted utilities
import {
  NODE_WIDTH,
  NODE_HEIGHT,
  NODE_HEIGHT_HOVERED_MIN,
  HOVER_UPWARD_SHIFT,
  CLEARANCE_BUFFER,
  HORIZONTAL_GAP,
  VERTICAL_GAP,
  CONNECTOR_NON_HIGHLIGHTED_OFFSET,
} from './utils/layoutConstants';

import { buildGraphData, calculateNodePositions } from './utils/graphMapping';
import { parseConditionToLabel } from './utils/nodeConfig';

// Re-export VisualizationLegend for external usage
export { VisualizationLegend };

// =============================================================================
// Clearance Calculation
// =============================================================================

/**
 * Calculate clearance offsets for nodes below hovered node.
 * Preserves expanded geometry during exit transitions.
 */
function calculateClearanceOffsets({
  geometryNodeId,
  exitingNodeId,
  hoveredNodeId,
  nodePositions,
  nodeHeights,
  getExpandedHeight,
}) {
  const offsets = new Map();
  
  if (!geometryNodeId) return offsets;
  
  const geometryPos = nodePositions.positions.get(geometryNodeId);
  if (!geometryPos) return offsets;
  
  // During exit, use the preserved expanded height
  // During hover, use the current measured height
  let actualExpandedHeight;
  if (exitingNodeId && !hoveredNodeId) {
    actualExpandedHeight = getExpandedHeight() || NODE_HEIGHT_HOVERED_MIN;
  } else {
    const measuredHeight = nodeHeights.get(geometryNodeId);
    actualExpandedHeight = measuredHeight || NODE_HEIGHT_HOVERED_MIN;
  }
  
  const geometryLevel = geometryPos.level;
  
  // Calculate expanded bounding box
  const expandedTop = geometryPos.y - HOVER_UPWARD_SHIFT;
  const expandedBottom = expandedTop + actualExpandedHeight;
  const centerX = geometryPos.x + geometryPos.width / 2;
  const expandedHalfWidth = 260 / 2; // NODE_WIDTH_HOVERED
  const expandedLeft = centerX - expandedHalfWidth;
  const expandedRight = centerX + expandedHalfWidth;
  
  const nodesToShift = [];
  
  // Find nodes that need clearance
  nodePositions.positions.forEach((pos, nodeId) => {
    if (nodeId === geometryNodeId) return;
    if (pos.level !== geometryLevel + 1) return;
    
    const nodeLeft = pos.x;
    const nodeRight = pos.x + pos.width;
    const nodeTop = pos.y;
    const nodeCenterX = pos.x + pos.width / 2;
    
    const hasHorizontalOverlap = !(nodeRight < expandedLeft || nodeLeft > expandedRight);
    if (!hasHorizontalOverlap) return;
    
    const overlapAmount = expandedBottom - nodeTop;
    if (overlapAmount > 0) {
      const dynamicOffset = overlapAmount + CLEARANCE_BUFFER;
      nodesToShift.push({ nodeId, offset: dynamicOffset, centerX: nodeCenterX, level: pos.level });
      offsets.set(nodeId, dynamicOffset);
    }
  });
  
  // Propagate to downstream levels
  if (nodesToShift.length > 0) {
    const maxOffset = Math.max(...nodesToShift.map(n => n.offset));
    nodePositions.positions.forEach((pos, nodeId) => {
      if (offsets.has(nodeId)) return;
      if (pos.level <= geometryLevel + 1) return;
      const nodeCenterX = pos.x + pos.width / 2;
      const isInShiftPath = nodesToShift.some(shifted => {
        const horizontalDistance = Math.abs(nodeCenterX - shifted.centerX);
        return horizontalDistance < (NODE_WIDTH + HORIZONTAL_GAP) / 2;
      });
      if (isInShiftPath) {
        offsets.set(nodeId, maxOffset);
      }
    });
  }
  
  return offsets;
}

// =============================================================================
// Main Visualization Component
// =============================================================================

export default function FunnelVisualization({
  nodes = [],
  edges = [],
  entryNodeId,
  selectedNodeId,
  onNodeClick,
}) {
  // Use extracted viewport hook for hover/exit state management
  const {
    hoveredNodeId,
    exitingNodeId,
    geometryNodeId,
    nodeHeights,
    handleNodeHover,
    handleCardHeightChange,
    getExpandedHeight,
    nodeHeightsRef,
  } = useFunnelViewport();
  
  // Build graph structure using extracted utility
  const graphData = useMemo(() => {
    return buildGraphData(nodes, edges, entryNodeId);
  }, [nodes, edges, entryNodeId]);
  
  // Calculate base positions using extracted utility
  const nodePositions = useMemo(() => {
    return calculateNodePositions(graphData, {
      NODE_WIDTH,
      NODE_HEIGHT,
      HORIZONTAL_GAP,
      VERTICAL_GAP,
    });
  }, [graphData]);
  
  // Calculate clearance offsets
  const clearanceOffsets = useMemo(() => {
    return calculateClearanceOffsets({
      geometryNodeId,
      exitingNodeId,
      hoveredNodeId,
      nodePositions,
      nodeHeights,
      getExpandedHeight,
    });
  }, [geometryNodeId, exitingNodeId, hoveredNodeId, nodePositions, nodeHeights, getExpandedHeight]);
  
  // Use extracted interactions hook
  const {
    handleNodeClick,
    isEdgeHighlighted,
    getActualNodeHeight,
    getAdjustedPosition,
  } = useFunnelInteractions({
    selectedNodeId,
    hoveredNodeId,
    exitingNodeId,
    geometryNodeId,
    nodeHeights,
    nodeHeightsRef,
    expandedHeight: getExpandedHeight(),
    onNodeClick,
  });
  
  // Get node content for hover preview
  const getNodeContent = useCallback((nodeId) => {
    const node = graphData.nodeMap.get(nodeId);
    if (!node) return { responses: [], message: '' };
    const responses = node.content?.responses || [];
    const message = node.content?.message || node.content?.title || node.content?.consent_text || '';
    return { responses, message };
  }, [graphData]);
  
  // Empty state
  if (nodes.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <GitBranch className="h-10 w-10 text-slate-600 mx-auto mb-2" />
          <p className="text-slate-400 text-sm">No steps to visualize</p>
          <p className="text-slate-500 text-xs mt-0.5">Add steps to see the funnel flow</p>
        </div>
      </div>
    );
  }
  
  // Calculate total height including potential clearance offsets
  const maxOffset = Math.max(0, ...Array.from(clearanceOffsets.values()));
  const adjustedTotalHeight = nodePositions.totalHeight + maxOffset;
  
  return (
    <div 
      className={cn(
        'h-full overflow-auto flex justify-center',
        '[&::-webkit-scrollbar]:w-2',
        '[&::-webkit-scrollbar-track]:bg-slate-900/50',
        '[&::-webkit-scrollbar-track]:rounded-full',
        '[&::-webkit-scrollbar-thumb]:bg-slate-700/60',
        '[&::-webkit-scrollbar-thumb]:rounded-full',
        '[&::-webkit-scrollbar-thumb]:border-2',
        '[&::-webkit-scrollbar-thumb]:border-slate-900/50',
        '[&::-webkit-scrollbar-thumb:hover]:bg-slate-600/70'
      )}
      data-testid="funnel-visualization"
    >
      <div 
        className="relative py-8 transition-all duration-150 ease-out"
        style={{
          width: nodePositions.totalWidth,
          minWidth: nodePositions.totalWidth,
          height: adjustedTotalHeight,
          minHeight: adjustedTotalHeight,
        }}
      >
        {/* SVG for connectors */}
        <svg 
          className="absolute inset-0 pointer-events-none transition-all duration-150 ease-out"
          width={nodePositions.totalWidth}
          height={adjustedTotalHeight}
          style={{ overflow: 'visible' }}
        >
          {/* Non-highlighted edges */}
          {graphData.visualEdges
            .filter(edge => !isEdgeHighlighted(edge))
            .map((edge, index) => {
              const sourceBasePos = nodePositions.positions.get(edge.source_node_id);
              const targetBasePos = nodePositions.positions.get(edge.target_node_id);
              
              if (!sourceBasePos || !targetBasePos) return null;
              if (!graphData.nodeMap.has(edge.source_node_id)) return null;
              if (!graphData.nodeMap.has(edge.target_node_id)) return null;
              
              const sourcePos = getAdjustedPosition(edge.source_node_id, sourceBasePos, clearanceOffsets);
              const targetPos = getAdjustedPosition(edge.target_node_id, targetBasePos, clearanceOffsets);
              
              const isSourceInGeometryState = edge.source_node_id === geometryNodeId;
              const isSourceSelected = edge.source_node_id === selectedNodeId;
              const nonHighlightedAdjustment = isSourceSelected ? 0 : CONNECTOR_NON_HIGHLIGHTED_OFFSET;
              const sourceActualHeight = getActualNodeHeight(edge.source_node_id, isSourceInGeometryState, isSourceSelected) + nonHighlightedAdjustment;
              
              const startX = sourcePos.x + sourcePos.width / 2;
              const startY = sourcePos.y + sourceActualHeight;
              const endX = targetPos.x + targetPos.width / 2;
              const endY = targetPos.y;
              
              return (
                <g key={`edge-${edge.source_node_id}-${edge.target_node_id}-${index}`}>
                  <ConnectorLine
                    startX={startX}
                    startY={startY}
                    endX={endX}
                    endY={endY}
                    isHighlighted={false}
                    isHovered={false}
                  />
                </g>
              );
            })}
          
          {/* Highlighted edges */}
          {graphData.visualEdges
            .filter(edge => isEdgeHighlighted(edge))
            .map((edge, index) => {
              const sourceBasePos = nodePositions.positions.get(edge.source_node_id);
              const targetBasePos = nodePositions.positions.get(edge.target_node_id);
              
              if (!sourceBasePos || !targetBasePos) return null;
              if (!graphData.nodeMap.has(edge.source_node_id)) return null;
              if (!graphData.nodeMap.has(edge.target_node_id)) return null;
              
              const sourcePos = getAdjustedPosition(edge.source_node_id, sourceBasePos, clearanceOffsets);
              const targetPos = getAdjustedPosition(edge.target_node_id, targetBasePos, clearanceOffsets);
              
              const isSourceInGeometryState = edge.source_node_id === geometryNodeId;
              const isSourceSelected = edge.source_node_id === selectedNodeId;
              const sourceActualHeight = getActualNodeHeight(edge.source_node_id, isSourceInGeometryState, isSourceSelected);
              
              const isTargetInGeometryState = edge.target_node_id === geometryNodeId;
              
              const startX = sourcePos.x + sourcePos.width / 2;
              const startY = sourcePos.y + sourceActualHeight;
              const endX = targetPos.x + targetPos.width / 2;
              const endY = isTargetInGeometryState ? targetPos.y - HOVER_UPWARD_SHIFT : targetPos.y;
              
              const isHovered = hoveredNodeId && edge.source_node_id === hoveredNodeId;
              
              return (
                <g key={`edge-hl-${edge.source_node_id}-${edge.target_node_id}-${index}`}>
                  <ConnectorLine
                    startX={startX}
                    startY={startY}
                    endX={endX}
                    endY={endY}
                    isHighlighted={true}
                    isHovered={isHovered}
                  />
                </g>
              );
            })}
        </svg>
        
        {/* Branch labels — MA-FUNNEL-VIZ-046: Only show on routes to END - NOT QUALIFIED */}
        {graphData.visualEdges.map((edge, index) => {
          // Visibility rule: only label edges targeting END - NOT QUALIFIED nodes
          const targetNode = graphData.nodeMap.get(edge.target_node_id);
          if (!targetNode) return null;
          const isEndNotQualified = targetNode.content?.is_terminal === true 
            && targetNode.content?.outcome === "not_qualified";
          if (!isEndNotQualified) return null;
          
          const label = edge.condition || edge.label;
          const cleanLabel = parseConditionToLabel(label);
          if (!cleanLabel) return null;
          
          const sourceBasePos = nodePositions.positions.get(edge.source_node_id);
          const targetBasePos = nodePositions.positions.get(edge.target_node_id);
          if (!sourceBasePos || !targetBasePos) return null;
          
          const sourcePos = getAdjustedPosition(edge.source_node_id, sourceBasePos, clearanceOffsets);
          const targetPos = getAdjustedPosition(edge.target_node_id, targetBasePos, clearanceOffsets);
          
          // Calculate edge start/end (same geometry as ConnectorLine)
          const isSourceInGeometryState = edge.source_node_id === geometryNodeId;
          const isSourceSelected = edge.source_node_id === selectedNodeId;
          const sourceActualHeight = getActualNodeHeight(edge.source_node_id, isSourceInGeometryState, isSourceSelected);
          
          const startX = sourcePos.x + sourcePos.width / 2;
          const startY = sourcePos.y + sourceActualHeight;
          const endX = targetPos.x + targetPos.width / 2;
          const endY = targetPos.y;
          
          // Bezier point at t≈0.37 — upstream of midpoint, away from END badge
          const t = 0.37;
          const t1 = 1 - t;
          const deltaY = endY - startY;
          const deltaX = endX - startX;
          const verticalClearance = Math.max(15, Math.min(deltaY * 0.3, 35));
          const horizontalBias = Math.sign(deltaX) * Math.min(Math.abs(deltaX) * 0.2, 35);
          const cp1X = startX + horizontalBias;
          const cp1Y = startY + verticalClearance;
          const cp2X = endX - horizontalBias;
          const cp2Y = endY - verticalClearance;
          const labelX = t1*t1*t1*startX + 3*t1*t1*t*cp1X + 3*t1*t*t*cp2X + t*t*t*endX;
          const labelY = t1*t1*t1*startY + 3*t1*t1*t*cp1Y + 3*t1*t*t*cp2Y + t*t*t*endY - 4;
          
          return (
            <div
              key={`label-${edge.source_node_id}-${edge.target_node_id}-${index}`}
              className="absolute pointer-events-none transform -translate-x-1/2 -translate-y-1/2 z-10 transition-all duration-150 ease-out"
              style={{ left: labelX, top: labelY }}
            >
              <BranchLabel 
                label={label} 
                condition={edge.condition} 
                isHighlighted={isEdgeHighlighted(edge)}
              />
            </div>
          );
        })}
        
        {/* Nodes */}
        {Array.from(nodePositions.positions.entries()).map(([nodeId, pos]) => {
          const node = graphData.nodeMap.get(nodeId);
          if (!node) return null;
          
          const { responses, message } = getNodeContent(nodeId);
          const isBranching = graphData.branchingNodes.has(nodeId);
          const isTerminal = graphData.terminalNodes.has(nodeId);
          const clearanceOffset = clearanceOffsets.get(nodeId) || 0;
          
          return (
            <div
              key={nodeId}
              className="absolute transition-all duration-150 ease-out"
              style={{
                left: pos.x,
                top: pos.y,
                width: pos.width,
                height: pos.height,
              }}
            >
              <VisualizationNode
                node={node}
                isSelected={nodeId === selectedNodeId}
                isEntry={nodeId === entryNodeId}
                isSubmitStep={node.metadata?.is_submit_step === true}
                isTerminal={isTerminal}
                isBranching={isBranching}
                isHovered={nodeId === hoveredNodeId}
                isAnyNodeHovered={hoveredNodeId !== null}
                onClick={handleNodeClick}
                onHover={handleNodeHover}
                onCardHeightChange={handleCardHeightChange}
                responses={responses}
                message={message}
                clearanceOffset={clearanceOffset}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
